/**
 * One-time migration: moves inline base64 artwork dataUrls from the
 * persisted art store (localStorage) into IndexedDB. Runs on app startup;
 * no-op once everything is migrated.
 *
 * Why: the previous design stored full canvas PNGs as base64 strings inside
 * `Artwork.dataUrl`, which lives in `homeschool-art` in localStorage. With
 * the per-child cap of 50, a few medium-sized canvases were enough to push
 * the whole origin over the ~5MB localStorage quota and break unrelated
 * writes (e.g. `homeschool-progress` saves on game exit). Mirrors the
 * earlier photo-migration pattern.
 */
import { useArtStore } from '../store/artStore';
import { saveArt, ART_IDB_PREFIX } from './art-db';

export async function migrateArtworksToIndexedDB(): Promise<void> {
  const { artworks } = useArtStore.getState();

  const toMigrate = artworks.filter(
    a => a.dataUrl && !a.dataUrl.startsWith(ART_IDB_PREFIX)
  );

  if (toMigrate.length === 0) return;

  const updates: { id: string; newUrl: string }[] = [];
  for (const a of toMigrate) {
    try {
      const idbUrl = await saveArt(a.id, a.dataUrl);
      updates.push({ id: a.id, newUrl: idbUrl });
    } catch {
      console.warn(`Failed to migrate artwork ${a.id} to IndexedDB`);
    }
  }

  if (updates.length === 0) return;

  const updateMap = new Map(updates.map(u => [u.id, u.newUrl]));
  useArtStore.setState(s => ({
    artworks: s.artworks.map(a =>
      updateMap.has(a.id) ? { ...a, dataUrl: updateMap.get(a.id)! } : a
    ),
  }));

  console.log(`Migrated ${updates.length} artwork(s) to IndexedDB`);
}
